import React, { useMemo } from "react"
import { Bar } from "@nivo/bar"
import type { BarDatum } from "@nivo/bar"
import { ResponsiveWrapper } from "@nivo/core"
import type { TableModel } from "utils/tableModel"
import type { BarTile as BarTileDoc } from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import {
  chartCellLeaves,
  modelToBarSeries,
  shapeBarSeries,
} from "features/Dashboard/runtime/modelAdapters"
import { effectiveStatistic } from "features/Dashboard/runtime/tileView"
import {
  barPadding,
  bottomLegends,
  categoryTicks,
  CHAR_PX,
  CHART_INK,
  chartUnits,
  colourLookup,
  DIM_ALPHA,
  entityColours,
  formatTick,
  hiddenColumns,
  formatValue,
  labelInk,
  legendHeight,
  nivoTheme,
  prefersReducedMotion,
  RIGHT_LEGEND,
  rightLegends,
  stackEnds,
  targetMarker,
  truncateLabel,
  valueDomain,
  withAlpha,
} from "./chartTheme"
import type { LegendItem } from "./chartTheme"
import ChartTooltip from "./ChartTooltip.component"
import ChartFrame from "./ChartFrame.component"
import { useCategoryCrossFilter } from "./useCategoryCrossFilter"
import { useTileTrace } from "./useTileTrace"
import type { TraceTarget } from "./useTileTrace"
import { markId, splitIndex, useChartTraceMenu } from "./useChartTraceMenu"

const SHARE_NOTE =
  "The chart shows this number as a share of its stack; the trace follows the table's number."

interface Layout {
  margin: { top: number; right: number; bottom: number; left: number }
  ticks: { rotation: number; maxChars: number }
}

/** Room the chart keeps around the plot: legends and outside value labels. */
interface Extra {
  /** Height of a bottom legend, px. */
  bottom: number
  /** Width of a right legend, px. */
  right: number
  /** Value labels past the bar ends need room above (or right of) the bars. */
  outsideLabels: boolean
}

/**
 * Categories under the plot, rotated when crowded, legends around it. A
 * compact chart (a panel of a repeated tile) keeps tighter margins and
 * shorter rotated labels.
 */
const verticalLayout = (
  width: number,
  count: number,
  extra: Extra,
  compact: boolean
): Layout => {
  const ticks = categoryTicks(width - (compact ? 48 : 64) - extra.right, count)
  const rotated = ticks.rotation !== 0
  if (compact) {
    return {
      ticks: rotated ? { ...ticks, maxChars: 10 } : ticks,
      margin: {
        top: extra.outsideLabels ? 16 : 4,
        right: 6 + extra.right,
        bottom: (rotated ? 52 : 22) + extra.bottom,
        left: 40,
      },
    }
  }
  return {
    ticks,
    margin: {
      top: extra.outsideLabels ? 20 : 8,
      right: 12 + extra.right,
      bottom: (rotated ? 72 : 28) + extra.bottom,
      left: 52,
    },
  }
}

/**
 * Categories left of the plot in up to 40% of the width (180 px at most;
 * a compact chart 35%, 110 px at most).
 */
const horizontalLayout = (
  width: number,
  labels: string[],
  extra: Extra,
  compact: boolean
): Layout => {
  const room = compact
    ? Math.min(110, width * 0.35)
    : Math.min(180, width * 0.4)
  const maxChars = Math.max(4, Math.floor((room - 16) / CHAR_PX))
  const longest = Math.min(maxChars, Math.max(...labels.map((l) => l.length)))
  const left = Math.round(longest * CHAR_PX + 16)
  if (compact) {
    return {
      ticks: { rotation: 0, maxChars },
      margin: {
        top: 4,
        right: (extra.outsideLabels ? 40 : 8) + extra.right,
        bottom: 22 + extra.bottom,
        left: Math.max(32, left),
      },
    }
  }
  return {
    ticks: { rotation: 0, maxChars },
    margin: {
      top: 8,
      right: (extra.outsideLabels ? 48 : 16) + extra.right,
      bottom: 28 + extra.bottom,
      left: Math.max(40, left),
    },
  }
}

/**
 * A bar tile: categories along one axis of the table, one bar per series
 * (the other axis), grouped or stacked, vertical or horizontal. The chart
 * options add value labels, a dashed target line, a sort by the first
 * series, fixed axis ends, the legend's place and, when stacked, stacks
 * scaled to 100%. Series colours follow the dashboard's colour choices,
 * then the palette, worked out from `raw` (the tile's last result; the
 * view by default) so hiding or renaming never moves them. With a single
 * series, a category the dashboard's colours name takes its colour and the
 * other bars keep the series colour. The chart draws one unit, its first
 * row's: rows in another unit are left out and named under the plot, and
 * the axis, the labels, the target and the axis ends are in the drawn
 * unit. In View mode a bar click toggles a cross-filter on its category
 * and the other categories dim. `compact` draws one panel of a repeated
 * tile: no legend (the panels share one), no note of left-out rows (the
 * grid has one), tighter margins, and the value axis up to `axisMax`
 * (plotted units), the end every panel shares; `panel` names its category.
 * In both modes a right-click on a bar (or the context menu key on a
 * focused one) offers the trace of its number.
 */
const BarTile: React.FC<{
  tile: BarTileDoc
  model: TableModel
  raw?: TableModel
  compact?: boolean
  axisMax?: number
  panel?: string
}> = ({ tile, model, raw = model, compact = false, axisMax, panel }) => {
  const palette = useDashboardStore((s) => s.document.theme.chartPalette)
  const colourMap = useDashboardStore(
    (s) => s.document.theme.categoryColours
  )
  const controls = useDashboardStore((s) => s.tileControls[tile.id])
  const v = tile.visual
  const chart = tile.chart
  const statistic = effectiveStatistic(tile, controls)
  const categoryAxis = v.seriesAxis === "across" ? "down" : "across"
  const { emits, selected, toggle } = useCategoryCrossFilter(
    tile,
    model,
    categoryAxis
  )
  const { openTrace, panel: tracePanel } = useTileTrace(tile, panel)
  const { openMenu, menu } = useChartTraceMenu(openTrace)
  const series = useMemo(
    () =>
      shapeBarSeries(
        modelToBarSeries(model, {
          statistic,
          seriesAxis: v.seriesAxis,
          showTotals: v.showTotals,
          hiddenCategories: hiddenColumns(v),
        }),
        { mode: v.mode, normalize: chart.normalize, sort: chart.sort }
      ),
    [
      model,
      statistic,
      v.seriesAxis,
      v.showTotals,
      v.hiddenCategories,
      v.mode,
      chart.normalize,
      chart.sort,
    ]
  )
  const { measures, categories } = tile
  const colourOf = useMemo(
    () =>
      entityColours(raw, v.seriesAxis, palette, colourMap, {
        measures,
        labels: categories.labels,
      }),
    [raw, v.seriesAxis, palette, colourMap, measures, categories.labels]
  )
  const categoryColour = useMemo(
    () =>
      colourLookup(raw, categoryAxis, palette, colourMap, {
        measures,
        labels: categories.labels,
      }).mappedOf,
    [raw, categoryAxis, palette, colourMap, measures, categories.labels]
  )

  if (series.categories.length === 0 || series.series.length === 0) {
    return <p className="dq-tile__empty">No values to chart.</p>
  }

  // Positional keys: no series id can clash with the index field.
  const keys = series.series.map((_, i) => `s${i}`)
  const seriesOf = (key: string | number) =>
    series.series[keys.indexOf(String(key))]
  const categoryLabel = new Map(series.categories.map((c) => [c.id, c.label]))
  const labelOf = (id: string | number) =>
    categoryLabel.get(String(id)) ?? String(id)
  const rows: BarDatum[] = series.categories.map((c, ci) => {
    const row: BarDatum = { category: c.id }
    // A missing value draws no bar rather than a zero one.
    series.series.forEach((s, si) => {
      const value = s.values[ci]
      if (value !== null) row[keys[si]] = value
    })
    return row
  })
  const horizontal = v.orientation === "horizontal"
  // Nivo lays horizontal categories out bottom-up; the table reads top-down.
  const data = horizontal ? [...rows].reverse() : rows
  const legendItems: LegendItem[] = series.series.map((s, i) => ({
    id: keys[i],
    label: s.label,
    color: colourOf(s.id),
  }))
  const multi = keys.length > 1
  /**
   * A bar's colour: its series', or on a single-series chart its
   * category's when the dashboard's colours name that category.
   */
  const barColour = (seriesKey: string | number, category: string): string =>
    (multi ? undefined : categoryColour(category)) ??
    colourOf(seriesOf(seriesKey)?.id ?? "")
  const stacked = v.mode === "stacked"
  const innerPadding = multi ? 2 : 0
  const barsPerGroup = stacked ? 1 : keys.length
  const format = series.format
  const labels = series.categories.map((c) => c.label)
  const dataLabels = chart.dataLabels === "on"
  const legendAt = multi && !compact ? chart.legend : "none"
  const domain = valueDomain(
    stacked
      ? stackEnds(
          series.series.map((s) => s.values),
          series.categories.length
        )
      : series.series.flatMap((s) => s.values),
    chart,
    format,
    axisMax
  )
  const target = chart.target
  const markers = target
    ? [
        targetMarker(
          horizontal ? "x" : "y",
          chartUnits(target.value, format),
          target.label ||
            `Target ${formatValue(chartUnits(target.value, format), format)}`
        ),
      ]
    : []

  /** The number of the bar the event is on: its row in `data` and series key. */
  const traceTarget = (event: React.MouseEvent): TraceTarget | null => {
    const id = markId(event, "bar.item.")
    if (id === null) return null
    const [key, index] = splitIndex(id)
    const s = seriesOf(key)
    const categoryId = data[index]?.category
    if (!s || categoryId === undefined) return null
    const ci = series.categories.findIndex((c) => c.id === categoryId)
    const value = s.values[ci]
    const leaves = chartCellLeaves(model, {
      categoryAxis,
      categoryId: String(categoryId),
      seriesId: s.id,
      statistic,
    })
    if (value === null || value === undefined || !leaves) return null
    return {
      ...leaves,
      value: formatValue(value, format),
      note: stacked && chart.normalize ? SHARE_NOTE : undefined,
    }
  }

  return (
    <ChartFrame
      kind="bar"
      clickable={emits}
      omitted={compact ? [] : series.omitted}
      onContextMenu={(e) => openMenu(e, traceTarget(e))}
      trace={
        <>
          {menu}
          {tracePanel}
        </>
      }
    >
      <ResponsiveWrapper>
        {({ width, height }) => {
          const extra: Extra = {
            bottom:
              legendAt === "bottom" ? legendHeight(width, legendItems) : 0,
            right: legendAt === "right" ? RIGHT_LEGEND : 0,
            outsideLabels: dataLabels && !stacked,
          }
          const { margin, ticks } = horizontal
            ? horizontalLayout(width, labels, extra, compact)
            : verticalLayout(width, labels.length, extra, compact)
          const valueTicks = compact ? 4 : 5
          const along = horizontal
            ? height - margin.top - margin.bottom
            : width - margin.left - margin.right
          const categoryAxisProps = {
            tickSize: 0,
            tickPadding: 8,
            tickRotation: ticks.rotation,
            format: (id: string | number) =>
              truncateLabel(labelOf(id), ticks.maxChars),
          }
          const valueAxisProps = {
            tickSize: 0,
            tickPadding: 8,
            tickValues: valueTicks,
            format: (n: string | number) => formatTick(Number(n), format),
          }
          return (
            <Bar
              width={width}
              height={height}
              data={data}
              keys={keys}
              indexBy="category"
              layout={v.orientation}
              groupMode={v.mode}
              theme={nivoTheme}
              colors={(bar) => {
                const base = barColour(bar.id, String(bar.indexValue))
                return selected && !selected.has(String(bar.indexValue))
                  ? withAlpha(base, DIM_ALPHA)
                  : base
              }}
              borderRadius={4}
              padding={barPadding(
                along,
                labels.length,
                barsPerGroup,
                innerPadding
              )}
              innerPadding={innerPadding}
              margin={margin}
              valueFormat={(n) => formatValue(n, format)}
              minValue={domain.min}
              maxValue={domain.max}
              valueScale={{ type: "linear", nice: domain.nice }}
              enableLabel={dataLabels}
              label={(d) => formatValue(d.value, format)}
              // Grouped bars carry their value past the end, stacked
              // segments inside, skipped where the segment is too small.
              labelPosition={stacked ? "middle" : "end"}
              labelOffset={stacked ? 0 : 8}
              labelSkipWidth={stacked && horizontal ? 32 : 0}
              labelSkipHeight={stacked && !horizontal ? 14 : 0}
              labelTextColor={(d) => (stacked ? labelInk(d.color) : CHART_INK)}
              markers={markers}
              enableGridX={horizontal}
              enableGridY={!horizontal}
              gridXValues={horizontal ? valueTicks : undefined}
              gridYValues={horizontal ? undefined : valueTicks}
              axisLeft={horizontal ? categoryAxisProps : valueAxisProps}
              axisBottom={horizontal ? valueAxisProps : categoryAxisProps}
              legends={
                legendAt === "bottom"
                  ? bottomLegends(width, legendItems, margin.bottom)
                  : legendAt === "right"
                    ? rightLegends(legendItems)
                    : []
              }
              onClick={(d) => toggle(String(d.indexValue))}
              isInteractive
              animate={!prefersReducedMotion()}
              tooltip={({ id, value, indexValue }) => {
                const s = seriesOf(id)
                return (
                  <ChartTooltip
                    title={labelOf(indexValue)}
                    rows={[
                      {
                        key: String(id),
                        colour: barColour(id, String(indexValue)),
                        value: formatValue(value, format),
                        label: multi ? s?.label : undefined,
                      },
                    ]}
                  />
                )
              }}
              role="img"
              ariaLabel={tile.title ?? "Bar chart"}
              isFocusable
              barAriaLabel={(d) =>
                [
                  labelOf(d.indexValue),
                  multi ? seriesOf(d.id)?.label : undefined,
                  formatValue(d.value ?? 0, format),
                ]
                  .filter(Boolean)
                  .join(", ")
              }
            />
          )
        }}
      </ResponsiveWrapper>
    </ChartFrame>
  )
}

export default BarTile
